"use client";
import { activeSiteConfig } from "@config/site/site-config";
import type { AddressValidatorResult } from "../types";
import dynamic from "next/dynamic";
import type { DeliveryInitialValues } from "@lib/utils/checkoutHelper";
import { isFailureAddressValidatorResult, isMissingHouseNumberError } from "../asserters";

export type ValidationModalAbortResponse = {
    action: "cancel";
};
export type ValidationModalContinueResponse = {
    action: "continue";
};
export type ValidationModalUpdateResponse = {
    action: "update";
    address: DeliveryInitialValues;
};

export type ValidationModalResponse =
    | ValidationModalAbortResponse
    | ValidationModalContinueResponse
    | ValidationModalUpdateResponse;

export type ValidationModalProps = {
    isOpen: boolean;
    onClose: (response: ValidationModalResponse) => void;
    validationResult: AddressValidatorResult;
    userAddress: DeliveryInitialValues;
};

const UsValidationModal = dynamic(
    () =>
        import("./UsValidationModal/UsValidationModal").then((module) => module.UsValidationModal),
    {
        ssr: false,
    }
);

const MissingHouseNumberModal = dynamic(
    () =>
        import("./MissingHouseNumberModal/MissingHouseNumberModal").then(
            (module) => module.MissingHouseNumberModal
        ),
    {
        ssr: false,
    }
);

// A market with its own modal renders every error type through it, including a
// missing house number. Other markets fall back to the shared modal.
const componentMap = {
    US: UsValidationModal,
};

export const AddressValidationModal = (props: ValidationModalProps) => {
    const { validationResult } = props;

    const SpecificValidationModal = componentMap[activeSiteConfig.country];
    if (SpecificValidationModal) {
        return <SpecificValidationModal {...props} />;
    }

    if (
        isFailureAddressValidatorResult(validationResult) &&
        isMissingHouseNumberError(validationResult.error)
    ) {
        return <MissingHouseNumberModal {...props} />;
    }

    return null;
};
